import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ChevronLeft, ChevronRight, Loader2, Lightbulb, Eye, Tag, Target, Grid3x3, X, Edit2, Save, XCircle, Trash2 } from 'lucide-react';
import { HeaderActions } from '@/components/layout/HeaderActions';
import { useOrganization } from '@/hooks/useOrganization';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import contentLibraryService from '@/lib/api/contentLibrary';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';

const PresentationViewerPage: React.FC = () => {
  const { presentationId } = useParams<{ presentationId: string }>();
  const navigate = useNavigate();
  const { currentOrganization } = useOrganization();
  const queryClient = useQueryClient();
  const orgId = currentOrganization?.id;

  const [currentSlideIndex, setCurrentSlideIndex] = useState(0);
  const [showThumbnails, setShowThumbnails] = useState(false);
  const [activeTab, setActiveTab] = useState<'summary' | 'keypoints' | 'visual' | 'usage' | 'topics'>('summary');
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  // Edit state
  const [editTitle, setEditTitle] = useState('');
  const [editSummary, setEditSummary] = useState('');
  const [editKeyPoints, setEditKeyPoints] = useState('');
  const [editVisual, setEditVisual] = useState('');
  const [editUsage, setEditUsage] = useState('');
  const [editTopics, setEditTopics] = useState('');

  // Fetch presentation
  const { data: presentationData, isLoading, error } = useQuery({
    queryKey: ['organization-presentation', orgId],
    queryFn: async () => {
      if (!orgId) throw new Error('Organization ID required');
      const response = await contentLibraryService.getOrganizationPresentationDeck(orgId);
      if (!response.success || !response.data) {
        throw new Error(response.error || 'Failed to fetch presentation');
      }
      return response.data;
    },
    enabled: !!orgId,
    retry: false,
  });

  const presentation = presentationData?.deck ? {
    ...presentationData.deck,
    slides: (presentationData.slides || []).sort((a, b) => a.slide_number - b.slide_number)
  } : null;

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (isEditing) return; // Don't navigate while editing
      if (e.key === 'ArrowLeft') {
        goToPrevious();
      } else if (e.key === 'ArrowRight') {
        goToNext();
      } else if (e.key === 'Escape' && showThumbnails) {
        setShowThumbnails(false);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [currentSlideIndex, showThumbnails, isEditing]);

  // Update edit state when slide changes or presentation loads (but not while editing)
  useEffect(() => {
    if (presentation && presentation.slides[currentSlideIndex] && !isEditing) {
      const slide = presentation.slides[currentSlideIndex];
      setEditTitle(slide.title || '');
      setEditSummary(slide.content_summary || '');
      setEditKeyPoints(slide.key_points?.join('\n') || '');
      setEditVisual(slide.visual_description || '');
      setEditUsage(slide.use_case || '');
      setEditTopics(slide.topics?.join(', ') || '');
    }
  }, [currentSlideIndex, presentation, isEditing]);

  // Reset edit mode when changing slides
  useEffect(() => {
    setIsEditing(false);
  }, [currentSlideIndex]);

  const handleEditClick = () => {
    setIsEditing(true);
  };

  const handleCancelEdit = () => {
    // Restore original values
    if (presentation && presentation.slides[currentSlideIndex]) {
      const slide = presentation.slides[currentSlideIndex];
      setEditTitle(slide.title || '');
      setEditSummary(slide.content_summary || '');
      setEditKeyPoints(slide.key_points?.join('\n') || '');
      setEditVisual(slide.visual_description || '');
      setEditUsage(slide.use_case || '');
      setEditTopics(slide.topics?.join(', ') || '');
    }
    setIsEditing(false);
  };

  const handleSaveEdit = async () => {
    if (!presentation || !presentation.slides[currentSlideIndex]) return;

    const slide = presentation.slides[currentSlideIndex];
    const savedSlideIndex = currentSlideIndex; // Store current index
    setIsSaving(true);

    // Try different possible ID field names
    const slideId = slide.id || (slide as any).slide_id || (slide as any).slideId;

    if (!slideId) {
      console.error('No slide ID found in slide object:', slide);
      toast.error('Cannot save: Slide ID not found');
      setIsSaving(false);
      return;
    }

    try {
      const updates = {
        title: editTitle,
        content_summary: editSummary,
        key_points: editKeyPoints.split('\n').filter(point => point.trim() !== ''),
        visual_description: editVisual,
        use_case: editUsage,
        topics: editTopics.split(',').map(t => t.trim()).filter(t => t !== ''),
      };

      const response = await contentLibraryService.updateSlide(slideId, updates);

      if (response.success) {
        toast.success('Slide updated successfully');
        // Refetch presentation data
        await queryClient.invalidateQueries({ queryKey: ['organization-presentation', orgId] });
        // Ensure we're still on the same slide
        if (currentSlideIndex !== savedSlideIndex) {
          setCurrentSlideIndex(savedSlideIndex);
        }
        // Exit edit mode after a small delay
        setTimeout(() => {
          setIsEditing(false);
        }, 100);
      } else {
        toast.error(response.error || 'Failed to update slide');
      }
    } catch (error) {
      console.error('Error updating slide:', error);
      toast.error('Failed to update slide');
    } finally {
      setIsSaving(false);
    }
  };

  const handleDeleteDeck = async () => {
    if (!presentation) return;

    setIsDeleting(true);

    try {
      const response = await contentLibraryService.deleteDeck(presentation.deck_id);

      if (response.success && response.data) {
        toast.success(`Deck "${response.data.deck_title}" deleted successfully`, {
          description: `Deleted ${response.data.slides_deleted} slides and ${response.data.images_deleted} images`,
        });

        // Invalidate the presentation query
        await queryClient.invalidateQueries({ queryKey: ['organization-presentation', orgId] });

        // Navigate back to content library
        navigate('/content-library');
      } else {
        toast.error(response.error || 'Failed to delete deck');
        setIsDeleting(false);
      }
    } catch (error) {
      console.error('Error deleting deck:', error);
      toast.error('Failed to delete deck');
      setIsDeleting(false);
    }
  };

  if (isLoading || !currentOrganization) {
    return (
      <div className="flex min-h-0 flex-col">
        <div className="flex items-center justify-center h-full">
          <div className="text-center text-subtle">
            <Loader2 className="h-8 w-8 animate-spin mx-auto mb-4" />
            <p>Loading presentation...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex min-h-0 flex-col">
        <div className="px-4 sm:px-6 md:px-8 pt-2 pb-4">
          <div className="flex items-center gap-4">
            <HeaderActions />
          </div>
        </div>
        <div className="flex-1 flex items-center justify-center p-8">
          <Alert variant="destructive">
            <AlertDescription>
              Failed to load presentation: {error instanceof Error ? error.message : 'Unknown error'}
            </AlertDescription>
          </Alert>
        </div>
      </div>
    );
  }

  if (!presentation || !presentation.slides || presentation.slides.length === 0) {
    return (
      <div className="flex min-h-0 flex-col">
        <div className="px-4 sm:px-6 md:px-8 pt-2 pb-4">
          <div className="flex items-center gap-4">
            <HeaderActions />
          </div>
        </div>
        <div className="flex-1 flex items-center justify-center p-8">
          <div className="text-center text-subtle space-y-2">
            <Loader2 className="h-6 w-6 animate-spin mx-auto text-blue-600" />
            <p>Slides are still processing.</p>
            <p className="text-sm">This can take a few minutes. Please check back soon.</p>
          </div>
        </div>
      </div>
    );
  }

  const currentSlide = presentation.slides[currentSlideIndex];
  const totalSlides = presentation.slides.length;

  const goToPrevious = () => {
    setCurrentSlideIndex((prev) => (prev > 0 ? prev - 1 : totalSlides - 1));
  };

  const goToNext = () => {
    setCurrentSlideIndex((prev) => (prev < totalSlides - 1 ? prev + 1 : 0));
  };

  const goToSlide = (index: number) => {
    setCurrentSlideIndex(index);
    setShowThumbnails(false);
  };

  return (
    <div className="flex min-h-0 flex-col h-full">
      {/* Top Header */}
      <div className="px-4 sm:px-6 pt-2 pb-4 bg-card">
        <div className="flex flex-col gap-3">
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-3 min-w-0">
              <h1 className="font-semibold text-base truncate max-w-[70vw] sm:max-w-md">
                {presentation.title || presentation.filename}
              </h1>
              {presentation.is_active && (
                <Badge variant="default" className="text-xs">Active</Badge>
              )}
            </div>
            <HeaderActions />
          </div>
          <div className="flex items-center gap-2 overflow-x-auto whitespace-nowrap pb-1 sm:pb-0">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowThumbnails(!showThumbnails)}
            >
              <Grid3x3 className="h-4 w-4 mr-2" />
              All Slides
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowDeleteDialog(true)}
              className="text-red-600 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-950"
            >
              <Trash2 className="h-4 w-4 mr-2" />
              Delete Deck
            </Button>
          </div>
        </div>
      </div>

      {/* Main Content Area */}
      <div className="flex-1 overflow-y-auto flex flex-col">
        {/* Slide Display Area */}
        <div className="flex flex-col min-h-0">
          {/* Slide Viewer */}
          <div className="flex items-center justify-center p-4 sm:p-6 bg-gradient-to-br from-gray-50 to-gray-100 dark:from-zinc-900 dark:to-zinc-800">
            <div className="relative w-full max-w-5xl mx-auto flex items-center justify-center">
              {/* Slide Image */}
              <div className="w-full aspect-video flex items-center justify-center">
                {currentSlide.image_url ? (
                  <img
                    src={currentSlide.image_url}
                    alt={`Slide ${currentSlide.slide_number}`}
                    className="max-w-full max-h-full object-contain rounded-lg shadow-2xl"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center bg-white dark:bg-zinc-800 rounded-lg shadow-2xl">
                    <p className="text-gray-400">Slide not available</p>
                  </div>
                )}
              </div>

              {/* Navigation Arrows */}
              <button
                onClick={goToPrevious}
                className="absolute left-0 top-1/2 -translate-y-1/2 bg-white/90 dark:bg-zinc-800/90 hover:bg-white dark:hover:bg-zinc-700 text-gray-800 dark:text-gray-200 p-3 rounded-full shadow-lg transition-all hover:scale-110"
                aria-label="Previous slide"
              >
                <ChevronLeft className="h-6 w-6" />
              </button>
              <button
                onClick={goToNext}
                className="absolute right-0 top-1/2 -translate-y-1/2 bg-white/90 dark:bg-zinc-800/90 hover:bg-white dark:hover:bg-zinc-700 text-gray-800 dark:text-gray-200 p-3 rounded-full shadow-lg transition-all hover:scale-110"
                aria-label="Next slide"
              >
                <ChevronRight className="h-6 w-6" />
              </button>

              {/* Slide Counter */}
              <div className="absolute top-0 left-0 bg-black/70 text-white px-3 py-1.5 rounded-br-lg text-xs font-medium">
                {currentSlide.slide_number} / {totalSlides}
              </div>
            </div>
          </div>

          {/* Bottom Info Panel */}
          <div className="border-t card-border bg-card">
            <div className="px-4 sm:px-6 py-4 max-w-5xl mx-auto">
              <div className="flex items-center justify-between gap-3 mb-4">
                {isEditing ? (
                  <div className="flex-1 min-w-0 mr-4">
                    <label className="text-xs font-medium text-muted-foreground mb-1 block">Title</label>
                    <Input
                      value={editTitle}
                      onChange={(e) => setEditTitle(e.target.value)}
                      className="text-lg font-bold"
                      placeholder="Slide title"
                    />
                  </div>
                ) : (
                  <h2 className="text-lg font-bold truncate">{currentSlide.title}</h2>
                )}

                {/* Edit Controls */}
                <div className="flex items-center gap-2">
                  {isEditing ? (
                    <>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={handleCancelEdit}
                        disabled={isSaving}
                      >
                        <XCircle className="h-4 w-4 mr-2" />
                        Cancel
                      </Button>
                      <Button
                        variant="default"
                        size="sm"
                        onClick={handleSaveEdit}
                        disabled={isSaving}
                      >
                        {isSaving ? (
                          <>
                            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                            Saving...
                          </>
                        ) : (
                          <>
                            <Save className="h-4 w-4 mr-2" />
                            Save
                          </>
                        )}
                      </Button>
                    </>
                  ) : (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleEditClick}
                    >
                      <Edit2 className="h-4 w-4 mr-2" />
                      Edit
                    </Button>
                  )}
                </div>
              </div>

              {/* Tab Buttons */}
              <div className="flex gap-1 mb-4 bg-muted p-1 rounded-lg overflow-x-auto whitespace-nowrap">
                <button
                  onClick={() => setActiveTab('summary')}
                  className={`flex-1 px-3 py-2 text-sm font-medium rounded-md transition-colors ${
                    activeTab === 'summary'
                      ? 'bg-background text-foreground shadow-sm'
                      : 'text-muted-foreground hover:text-foreground'
                  }`}
                >
                  Summary
                </button>
                <button
                  onClick={() => setActiveTab('keypoints')}
                  className={`flex-1 px-3 py-2 text-sm font-medium rounded-md transition-colors ${
                    activeTab === 'keypoints'
                      ? 'bg-background text-foreground shadow-sm'
                      : 'text-muted-foreground hover:text-foreground'
                  }`}
                >
                  Key Points
                </button>
                <button
                  onClick={() => setActiveTab('visual')}
                  className={`flex-1 px-3 py-2 text-sm font-medium rounded-md transition-colors ${
                    activeTab === 'visual'
                      ? 'bg-background text-foreground shadow-sm'
                      : 'text-muted-foreground hover:text-foreground'
                  }`}
                >
                  Visual
                </button>
                <button
                  onClick={() => setActiveTab('usage')}
                  className={`flex-1 px-3 py-2 text-sm font-medium rounded-md transition-colors ${
                    activeTab === 'usage'
                      ? 'bg-background text-foreground shadow-sm'
                      : 'text-muted-foreground hover:text-foreground'
                  }`}
                >
                  Usage
                </button>
                <button
                  onClick={() => setActiveTab('topics')}
                  className={`flex-1 px-3 py-2 text-sm font-medium rounded-md transition-colors ${
                    activeTab === 'topics'
                      ? 'bg-background text-foreground shadow-sm'
                      : 'text-muted-foreground hover:text-foreground'
                  }`}
                >
                  Topics
                </button>
              </div>

              {/* Tab Content */}
              <div className="py-2">
                {activeTab === 'summary' && (
                  isEditing ? (
                    <div>
                      <label className="text-xs font-medium text-muted-foreground mb-1 block">Content Summary</label>
                      <Textarea
                        value={editSummary}
                        onChange={(e) => setEditSummary(e.target.value)}
                        className="min-h-[100px]"
                        placeholder="Describe the content of this slide..."
                      />
                    </div>
                  ) : (
                    <p className="text-subtle leading-relaxed text-sm">
                      {currentSlide.content_summary || 'No summary available'}
                    </p>
                  )
                )}

                {activeTab === 'keypoints' && (
                  isEditing ? (
                    <div>
                      <label className="text-xs font-medium text-muted-foreground mb-1 block">Key Points (one per line)</label>
                      <Textarea
                        value={editKeyPoints}
                        onChange={(e) => setEditKeyPoints(e.target.value)}
                        className="min-h-[120px]"
                        placeholder="Enter each key point on a new line..."
                      />
                    </div>
                  ) : (
                    <>
                      {currentSlide.key_points && currentSlide.key_points.length > 0 ? (
                        <ul className="space-y-2">
                          {currentSlide.key_points.map((point, idx) => (
                            <li key={idx} className="flex items-start gap-3">
                              <Lightbulb className="h-4 w-4 text-yellow-600 mt-0.5 shrink-0" />
                              <span className="text-sm">{point}</span>
                            </li>
                          ))}
                        </ul>
                      ) : (
                        <p className="text-subtle text-sm">No key points available</p>
                      )}
                    </>
                  )
                )}

                {activeTab === 'visual' && (
                  isEditing ? (
                    <div>
                      <label className="text-xs font-medium text-muted-foreground mb-1 block">Visual Description</label>
                      <Textarea
                        value={editVisual}
                        onChange={(e) => setEditVisual(e.target.value)}
                        className="min-h-[100px]"
                        placeholder="Describe the visual elements of this slide..."
                      />
                    </div>
                  ) : (
                    <div className="flex gap-2">
                      <Eye className="h-4 w-4 text-purple-600 shrink-0 mt-0.5" />
                      <p className="text-sm text-subtle leading-relaxed">
                        {currentSlide.visual_description || 'No visual description available'}
                      </p>
                    </div>
                  )
                )}

                {activeTab === 'usage' && (
                  isEditing ? (
                    <div>
                      <label className="text-xs font-medium text-muted-foreground mb-1 block">Use Case / When to Use</label>
                      <Textarea
                        value={editUsage}
                        onChange={(e) => setEditUsage(e.target.value)}
                        className="min-h-[100px]"
                        placeholder="Describe when and how to use this slide..."
                      />
                    </div>
                  ) : (
                    <>
                      {currentSlide.use_case ? (
                        <div className="flex gap-2">
                          <Target className="h-4 w-4 text-red-600 shrink-0 mt-0.5" />
                          <p className="text-sm text-subtle leading-relaxed">
                            {currentSlide.use_case}
                          </p>
                        </div>
                      ) : (
                        <p className="text-subtle text-sm">No usage information available</p>
                      )}
                    </>
                  )
                )}

                {activeTab === 'topics' && (
                  isEditing ? (
                    <div>
                      <label className="text-xs font-medium text-muted-foreground mb-1 block">Topics (comma-separated)</label>
                      <Input
                        value={editTopics}
                        onChange={(e) => setEditTopics(e.target.value)}
                        placeholder="e.g., pricing, features, comparison"
                      />
                    </div>
                  ) : (
                    <>
                      {currentSlide.topics && currentSlide.topics.length > 0 ? (
                        <div className="flex flex-wrap gap-1.5">
                          {currentSlide.topics.map((topic, idx) => (
                            <Badge key={idx} variant="secondary" className="text-xs">
                              {topic}
                            </Badge>
                          ))}
                        </div>
                      ) : (
                        <p className="text-subtle text-sm">No topics available</p>
                      )}
                    </>
                  )
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Thumbnails Sidebar Overlay */}
        {showThumbnails && (
          <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-8" onClick={() => setShowThumbnails(false)}>
            <div className="bg-card rounded-xl shadow-2xl max-w-5xl w-full max-h-[90vh] overflow-hidden" onClick={(e) => e.stopPropagation()}>
              <div className="flex items-center justify-between px-6 py-4 border-b card-border">
                <h3 className="font-semibold text-lg">All Slides ({totalSlides})</h3>
                <Button variant="ghost" size="sm" onClick={() => setShowThumbnails(false)}>
                  <X className="h-4 w-4" />
                </Button>
              </div>
              <div className="p-6 overflow-y-auto max-h-[calc(90vh-80px)]">
                <div className="grid grid-cols-4 gap-4">
                  {presentation.slides.map((slide, idx) => (
                    <button
                      key={slide.id}
                      onClick={() => goToSlide(idx)}
                      className={`group relative aspect-video rounded-lg border-2 transition-all overflow-hidden ${
                        idx === currentSlideIndex
                          ? 'border-blue-600 ring-2 ring-blue-200 dark:ring-blue-900'
                          : 'border-border hover:border-blue-400 hover:shadow-lg'
                      }`}
                    >
                      {slide.image_url ? (
                        <img
                          src={slide.image_url}
                          alt={`Slide ${slide.slide_number}`}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <div className="w-full h-full bg-gray-100 dark:bg-zinc-800 flex items-center justify-center">
                          <span className="text-gray-400 text-xs">No preview</span>
                        </div>
                      )}
                      <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/80 to-transparent p-2">
                        <p className="text-white text-xs font-medium truncate">{slide.title}</p>
                        <p className="text-white/70 text-xs">Slide {slide.slide_number}</p>
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Presentation Deck?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete "{presentation?.title || presentation?.filename}" and all {presentation?.total_slides} slides.
              This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isDeleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteDeck}
              disabled={isDeleting}
              className="bg-red-600 hover:bg-red-700 focus:ring-red-600"
            >
              {isDeleting ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Deleting...
                </>
              ) : (
                <>
                  <Trash2 className="h-4 w-4 mr-2" />
                  Delete Deck
                </>
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default PresentationViewerPage;
